import { type ChangeEvent, type FocusEvent, forwardRef, type ReactNode, useState } from 'react'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { Label } from '../ui/label'
import { Input, type InputProps } from '../ui/input'
import { Typography } from '@/components/ui/typography'
import FieldInfo from './FieldInfo'

export type DecimalInputValue = string | number | null | undefined

type DecimalPlacesOptions = {
  decimalPlaces?: number
}

const roundToDecimalPlaces = (value: number, decimalPlaces: number) => {
  const factor = 10 ** decimalPlaces
  return Math.round((value + Number.EPSILON) * factor) / factor
}

export const sanitizeDecimalInputValue = (
  value: DecimalInputValue,
  { decimalPlaces = 1 }: DecimalPlacesOptions = {}
) => {
  if (value === '' || value === null || value === undefined) {
    return ''
  }

  const normalized = String(value).replace(/[^0-9.]/g, '')
  if (!normalized) {
    return ''
  }

  const [integerRaw = '', ...decimalParts] = normalized.split('.')
  const hasDecimal = normalized.includes('.')
  const integerPart = integerRaw.replace(/^0+(?=\d)/, '')

  if (!hasDecimal || decimalPlaces <= 0) {
    return integerPart || '0'
  }

  const decimalPart = decimalParts.join('').slice(0, decimalPlaces)
  return `${integerPart || '0'}.${decimalPart}`
}

export const parseDecimalInputValue = (value: DecimalInputValue) => {
  if (value === '' || value === null || value === undefined) {
    return null
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export const formatDecimalInputValue = (
  value: DecimalInputValue,
  { decimalPlaces = 1 }: DecimalPlacesOptions = {}
) => {
  const number = parseDecimalInputValue(value)
  if (number === null) {
    return ''
  }

  return String(Number(roundToDecimalPlaces(number, decimalPlaces).toFixed(decimalPlaces)))
}

export type DecimalInputProps = Omit<InputProps, 'value'> & {
  decimalPlaces?: number
  value?: DecimalInputValue
  onValueChange?: (value: number | null) => void
  onBlurValueChange?: (value: number | null) => void
}

const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(function DecimalInput(
  {
    decimalPlaces = 1,
    min = 0,
    step,
    type = 'number',
    value,
    onValueChange,
    onBlurValueChange,
    onBlur,
    onChange,
    onFocus,
    ...props
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false)
  const formattedValue = formatDecimalInputValue(value, { decimalPlaces })
  const [draftState, setDraftState] = useState(() => ({
    formattedValue,
    value: '',
  }))
  let draftValue = draftState.value

  if (draftState.formattedValue !== formattedValue) {
    draftValue = formattedValue
    setDraftState({
      formattedValue,
      value: formattedValue,
    })
  }

  const displayValue = isFocused ? draftValue : formattedValue
  const nativeStep = step ?? (decimalPlaces > 0 ? 'any' : 1)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = sanitizeDecimalInputValue(event.target.value, { decimalPlaces })
    setDraftState((current) => ({
      ...current,
      value: nextValue,
    }))
    onValueChange?.(parseDecimalInputValue(nextValue))
    onChange?.(event)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const nextValue = formatDecimalInputValue(draftValue, { decimalPlaces })
    const parsedValue = parseDecimalInputValue(nextValue)

    setIsFocused(false)
    setDraftState((current) => ({
      ...current,
      value: nextValue,
    }))
    onBlurValueChange?.(parsedValue)
    onBlur?.(event)
  }

  return (
    <Input
      ref={ref}
      type={type}
      inputMode="decimal"
      min={min}
      step={nativeStep}
      value={displayValue}
      onChange={handleChange}
      onFocus={(event) => {
        setIsFocused(true)
        setDraftState({
          formattedValue,
          value: formattedValue,
        })
        onFocus?.(event)
      }}
      onBlur={handleBlur}
      {...props}
    />
  )
})

export type DecimalFieldInputProps = DecimalInputProps & {
  label?: ReactNode
  labelEnd?: ReactNode
  readOnlyEmptyText?: ReactNode
}

export function DecimalFieldInput({
  id,
  label,
  labelEnd,
  type,
  className,
  readOnlyEmptyText = '',
  ...props
}: DecimalFieldInputProps) {
  const field = useFieldContext<DecimalInputValue>()
  const { isReadOnly } = useIsReadOnly()

  return (
    <div className="flex w-full flex-col gap-y-2">
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={id || field.name}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="supporting" tone={!field.state.value ? 'muted' : 'default'}>
          {field.state.value ?? readOnlyEmptyText}
        </Typography>
      ) : (
        <DecimalInput
          id={id || field.name}
          value={field.state.value}
          onValueChange={field.handleChange}
          onBlurValueChange={field.handleChange}
          onBlur={field.handleBlur}
          className={className}
          {...props}
        />
      )}

      {type !== 'hidden' && <FieldInfo />}
    </div>
  )
}

export default DecimalInput
